import type { Brand, Country, Language } from '@seek/melways-sites';
import { adapter } from '@seek/tealium-adapter';

type TrackArguments = {
  eventName: string;
} & Record<string, unknown>;
type TrackLink = (trackLinkArguments: TrackArguments) => void;
type TrackView = (trackViewArguments: TrackArguments) => void;
type SetState = (newState: Partial<AnalyticsClientState>) => void;

interface AnalyticsClient {
  trackLink: TrackLink;
  trackView: TrackView;
  setState: SetState;
}

interface AnalyticsClientState {
  currentPage: 'my activity';
  brand: Brand;
  country?: Country;
  siteLanguage: Language;
  loginId?: string;
  profileGuid?: string;
  currentPageSection?: 'saved jobs' | 'applied jobs';
  currentPageSubsection?: 'view application';
  isLoggedIn: boolean;
  siteCountry: string | null;
  siteSection?: 'discover' | 'apply';
  zone: string;
}

export const initialState = {
  currentPage: 'my activity' as const,
  brand: 'seek' as const,
  siteLanguage: 'en' as const,
  isLoggedIn: false,
  zone: 'anz-1',
  siteCountry: 'au',
};

// Only use for testing as we want a singleton for the analytics client
export const createAnalyticsClient: () => AnalyticsClient = () => {
  let state: AnalyticsClientState = initialState;
  const analytics = adapter();

  const trackLink: TrackLink = ({ eventName, ...eventData }) => {
    analytics.link(eventName, {
      linkName: eventName,
      ...state,
      ...eventData,
    });
  };

  const trackView: TrackView = ({ eventName, ...eventData }) => {
    analytics.view(eventName, {
      ...state,
      ...eventData,
    });
  };

  const setState: SetState = (newState) => {
    state = { ...state, ...newState };
  };

  return {
    trackView,
    trackLink,
    setState,
  };
};

export const analyticsClient = createAnalyticsClient();
